<template>
    <div :id="`emailRecipients${hashPanel}`">
        <EmailInput
            v-show="!oneInputDisplayed"
            ref="inputAllEmail"
            :label="labelAllInput"
            :emailAddresses="allEmails"
            type="all"
            :shouldHideInput="allEmails.length > 0"
            @onAddTag="onAddTag"
            @focus="onFocusInputAllEmail"
        >
            <template #tags>
                <CompassTagInteractive
                    v-for="emailAddress in allEmailsDisplayed"
                    :ref="`tagInteractive${emailAddress.id}`"
                    :key="emailAddress.id"
                    size="small"
                    :color="emailAddress.isValid ? 'blue' : 'red'"
                    :modelValue="emailAddress.email"
                    :valueLabel="emailAddress.email"
                    @focus="onFocusInputAllEmail"
                    @tag-remove="onRemoveTag(emailAddress, { id: 'all' })"
                />
                <CompassPlainButton
                    v-show="allEmailsDisplayed.length !== allEmails.length"
                    :label="labelMoreTags"
                    size="small"
                    @click="displayAllInputs"
                />
            </template>
            <template slot="actions">
                <CompassPlainButton
                    :label="$t.__('email.emailPanels.EmailRecipientsInputs.more_recipients_button')"
                    size="small"
                    variant="neutral"
                    @click="displayAllInputs(!allInputsDisplayed)"
                />
            </template>
        </EmailInput>
        <EmailInput
            v-for="item in inputs"
            v-show="item.isVisible"
            :key="item.id"
            :ref="`emailInput${item.id}`"
            :label="getLabelEmailInput(item)"
            :emailAddresses="allEmails"
            :type="item.id"
            class="input-recipients"
            :isFocused="item.isFocused"
            :hashPanel="hashPanel"
            @onAddTag="onAddTag"
            @focus="onFocusInputEmail(item)"
            @blur="(isFilled) => onBlur(isFilled, item)"
        >
            <template #tags>
                <CompassTagInteractive
                    v-for="emailAddress in emails[item.id]"
                    :ref="`tagInteractive${emailAddress.id}`"
                    :key="emailAddress.id"
                    size="small"
                    :color="emailAddress.isValid ? 'blue' : 'red'"
                    :modelValue="emailAddress.email"
                    :valueLabel="emailAddress.email"
                    @tag-remove="onRemoveTag(emailAddress, item)"
                />
            </template>
            <template #actions v-if="lastInputOpened === item.id">
                <CompassPlainButton
                    :label="$t.__('email.emailPanels.EmailRecipientsInputs.more_recipients_button')"
                    size="small"
                    variant="neutral"
                    @click="displayAllInputs(!allInputsDisplayed)"
                />
            </template>
        </EmailInput>
    </div>
</template>
<script>
import generateHash from 'Helpers/hashGenerator.js';
import EmailInput from 'vcomponents/email/emailPanels/EmailInput.vue';
const HEIGHT_EXPECTED_DIV_INPUT = 56;

export default {
    components: {
        EmailInput,
    },
    props: {
        hashPanel: {
            type: String,
            required: true,
        },
        recipients: {
            type: Object,
            default: () => ({
                to: [],
                cc: [],
                bcc: [],
            }),
        },
        isUpdatingRecipients: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            emails: {
                to: [],
                cc: [],
                bcc: [],
            },
            typeRecipients: {
                TO: 'to',
                CC: 'cc',
                BCC: 'bcc',
                ALL: 'all',
            },
            currentInputFocused: '',
            inputs: [
                {
                    id: 'to',
                    label: this.$l10n.__('À'),
                    isFocused: false,
                    isFilled: false,
                    isVisible: false,
                },
                {
                    id: 'cc',
                    label: this.$l10n.__('Cc'),
                    isVisible: false,
                    isFocused: false,
                    isFilled: false,
                },
                {
                    id: 'bcc',
                    label: this.$l10n.__('Cci'),
                    isVisible: false,
                    isFocused: false,
                    isFilled: false,
                },
            ],
            lastInputOpened: null,
            allEmailsDisplayed: [],
        };
    },
    computed: {
        labelAllInput() {
            if (this.emails['to'].length === 0 && this.emails['bcc'].length > 0) {
                return this.$l10n.__('Cci');
            }
            return this.$l10n.__('À');
        },
        labelMoreTags() {
            const count = this.allEmails.length - this.allEmailsDisplayed.length;
            return this.$l10n.n__('+ %s destinataire', '+ %s destinataires', count, count);
        },
        allEmails() {
            return [...this.emails.to, ...this.emails.cc, ...this.emails.bcc];
        },
        oneInputDisplayed() {
            return this.inputs.some((input) => input.isVisible);
        },
        allInputsDisplayed() {
            return this.inputs.every((input) => input.isVisible);
        },
    },
    watch: {
        recipients: {
            handler(newRecipients) {
                if (this.isUpdatingRecipients) {
                    const recipientsLength = Object.values(newRecipients).reduce(
                        (total, record) => (total += record.length),
                        0,
                    );
                    if (recipientsLength) {
                        this.formatIncomingEmailAdresses();
                    }
                }
                this.$emit('updateRecipient');
            },
            deep: true,
            immediate: true,
        },
    },
    created() {
        const recipientsLength = Object.values(this.recipients).reduce(
            (total, record) => (total += record.length),
            0,
        );
        if (recipientsLength) {
            this.formatIncomingEmailAdresses();
        }
        EventBus.on('validateEmail', this.onClickOutsideEmailRecipients);
    },
    mounted() {
        document.addEventListener('click', this.onClickOutsideEmailRecipients);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onClickOutsideEmailRecipients);
    },
    methods: {
        formatIncomingEmailAdresses() {
            const recipientsArray = Object.entries(this.recipients);

            for (const [key, value] of recipientsArray) {
                const input = this.inputs.find((item) => item.id === key);

                if (value.length > 0) {
                    input.isVisible = true;
                    input.isFilled = true;
                } else {
                    input.isVisible = false;
                    input.isFilled = false;
                }

                this.emails[key] = value.map((item) => {
                    return {
                        ...item,
                        email: item.email,
                        id: generateHash(),
                        isValid: true,
                        type: key,
                    };
                });
            }

            this.setAllInputBlurred();
        },
        onClickOutsideEmailRecipients() {
            const target = document.getElementById(`emailRecipients${this.hashPanel}`);
            const dropdown = document.getElementById(`inputDropdown${this.hashPanel}`);
            if (
                !target?.contains(event.target) &&
                !dropdown?.contains(event.target) &&
                !event.target.closest('.dropdown-email__option')
            ) {
                this.setAllInputBlurred();
                this.lastInputOpened = null;
            }
        },
        setAllInputBlurred() {
            for (const input of this.inputs) {
                input.isFocused = false;
                input.isVisible = false;
            }
            this.currentInputFocused = 'to';
            this.lastInputOpened = 'to';
            this.$emit('updateAddressesEmails', this.allEmails);
            this.$nextTick(() => this.handleTagsContainer());
            if (!this.allInputsDisplayed) {
                this.lastInputOpened = null;
            }
        },
        handleTagsContainer() {
            this.allEmailsDisplayed = [...this.allEmails];
            const div = this.$refs.inputAllEmail;
            let divHeight = div?.$el.clientHeight;
            for (let i = 0; i < this.allEmails.length; i++) {
                requestAnimationFrame(() => {
                    divHeight = div?.$el.clientHeight;
                    if (divHeight > HEIGHT_EXPECTED_DIV_INPUT) {
                        this.allEmailsDisplayed.pop();
                    }
                });
            }
        },
        onFocusInputAllEmail() {
            for (const input of this.inputs) {
                input.isFocused = false;
                input.isVisible = false;

                if (input.id === 'to') {
                    input.isFocused = true;
                }
            }
            this.displayAllInputs();
        },
        onFocusInputEmail(item) {
            this.currentInputFocused = item.id;
            for (const input of this.inputs) {
                if (input.id === item.id) {
                    input.isFocused = true;
                    input.isVisible = true;
                } else {
                    this.onBlur(input.isFilled, input);
                }
            }
            if (this.lastInputOpened === 'to' && item.id !== 'to') {
                this.lastInputOpened = item.id;
            } else if (this.lastInputOpened === 'cc' && !['to', 'cc'].includes(item.id)) {
                this.lastInputOpened = item.id;
            } else if (this.lastInputOpened === null) {
                this.lastInputOpened = item.id;
            }
        },
        onBlur(isFilled, item) {
            item.isFilled = isFilled;
            item.isFocused = false;
            this.$nextTick(() => {
                if (!this.allInputsDisplayed) {
                    this.lastInputOpened = null;
                }
            });
        },
        displayAllInputs(newDisplayValue = true) {
            for (const input of this.inputs) {
                input.isVisible = newDisplayValue;
            }
        },
        onRemoveTag(item, input) {
            const index = this.emails[item.type].findIndex((email) => email.id === item.id);
            this.emails[item.type].splice(index, 1);
            this.inputs.map((input) => {
                if (input.id === item.type) {
                    input.isFilled = !!this.emails[item.type].length;
                }
                return input;
            });
            if (this.allEmails.length && input.id !== this.typeRecipients.ALL) {
                this.$nextTick(() => {
                    this.onFocusInputAllEmail();
                });
            }
        },
        onAddTag(email) {
            this.emails[email.type].push(email);
            this.$emit('updateAddressesEmails', this.allEmails);
        },
        getLabelEmailInput(item) {
            if (item.isFocused || item.isFilled || ['cc', 'bcc'].includes(item.id)) {
                return item.label;
            } else {
                return !this.allInputsDisplayed ? this.$l10n.__('Destinataires') : item.label;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.input-recipients:not(:last-child) {
    margin-bottom: var(--spacing-0);
}
.input-actions {
    color: var(--colors-common-text-subtle-default);
    &:hover {
        color: var(--colors-common-text-subtle-hover);
    }
}
</style>
